import type { LinkEmailAvailabilityDto, LinkEmailResult, Locale } from "@shelflist/shared";
import { useQuery } from "@tanstack/react-query";
import { CircleCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { useErrorText } from "@/lib/errors";
import { adminEmailQuery } from "@/lib/kindle";

/** Shared pieces of the invitation and reset link dialogs (sending a link by email). */

/** Language names are shown in their own language so they stay recognizable in any UI language. */
const LOCALE_NAMES: Record<Locale, string> = { de: "Deutsch", en: "English" };

export function localeName(locale: Locale): string {
  return LOCALE_NAMES[locale];
}

/** The admin email status, which tells whether links can be sent by email. */
export function useLinkEmails(): LinkEmailAvailabilityDto {
  const { data } = useQuery(adminEmailQuery);
  // While loading (or if the request failed) behave as before: no email options.
  return data?.linkEmails ?? { available: false, reason: "email_not_configured" };
}

/** The UI language reduced to a supported email language. */
export function useUiLocale(): Locale {
  const { i18n } = useTranslation();
  return i18n.language.toLowerCase().startsWith("de") ? "de" : "en";
}

export function LocaleSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: Locale;
  onChange: (locale: Locale) => void;
}) {
  const { t } = useTranslation("admin");
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{t("invitations.sendLanguage")}</Label>
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value as Locale)}>
        {(Object.keys(LOCALE_NAMES) as Locale[]).map((locale) => (
          <option key={locale} value={locale}>
            {LOCALE_NAMES[locale]}
          </option>
        ))}
      </NativeSelect>
    </div>
  );
}

export function CheckboxRow({
  id,
  checked,
  disabled,
  onChange,
  label,
  hint,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <div className="grid gap-1">
      <label htmlFor={id} className="flex min-h-8 items-start gap-2 text-sm">
        <input
          id={id}
          type="checkbox"
          className="accent-primary mt-1 size-4 shrink-0"
          checked={checked}
          disabled={disabled}
          aria-describedby={hint ? `${id}-hint` : undefined}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="break-words">{label}</span>
      </label>
      {hint ? (
        <p id={`${id}-hint`} className="text-muted-foreground pl-6 text-sm">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Translated reason for a failed send; the diagnostic follows separately. */
export function useDeliveryErrorText() {
  const errorText = useErrorText();
  return (code: string | null | undefined): string => errorText(code ?? undefined);
}

/** Outcome of a send: success message or the translated error with the diagnostic. */
export function DeliveryAlert({
  result,
  success,
  failed,
}: {
  result: LinkEmailResult;
  /** Already translated success text. */
  success: string;
  /** Builds the translated failure text from the translated error. */
  failed: (error: string) => string;
}) {
  const errorText = useDeliveryErrorText();
  if (result.status === "sent") {
    return (
      <Alert role="status">
        <CircleCheck aria-hidden />
        <AlertDescription className="text-foreground">{success}</AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert variant="destructive">
      <AlertDescription>
        <p>{failed(errorText(result.code))}</p>
        {result.diagnostic ? <code className="text-xs break-all">{result.diagnostic}</code> : null}
      </AlertDescription>
    </Alert>
  );
}
